import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

const AnalyticsTab = ({ contextKey, appUid, accountId }) => {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ contextKey, appUid, period, accountId });
        const res = await fetch(`/admin/analytics?${qs}`);
        const json = await res.json();
        setData(res.ok ? json : null);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    if (contextKey && appUid && accountId) load();
  }, [contextKey, appUid, accountId, period]);

  if (loading) return <p className="admin-loading">Загрузка...</p>;
  if (!data) return <p className="admin-error">Ошибка загрузки</p>;

  const regChart = (data.registrationsByDay || []).map((r) => ({ date: r._id.slice(5), count: r.count }));
  const salesChart = (data.salesByDay || []).map((r) => ({ date: r._id.slice(5), revenue: r.revenue, count: r.count }));
  const bonusChart = (data.bonusByDay || []).map((r) => ({
    date: r._id.slice(5),
    earned: r.earned,
    spent: r.spent,
  }));

  return (
    <div className="admin-section">
      <div className="admin-row-between">
        <h2>Аналитика</h2>
        <select className="admin-select-sm" value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="7d">7 дней</option>
          <option value="30d">30 дней</option>
          <option value="90d">90 дней</option>
        </select>
      </div>

      <div className="admin-stats-grid">
        <div className="admin-stat-card"><b>{data.activeCards}</b><span>Карт</span></div>
        <div className="admin-stat-card"><b>{data.newCards}</b><span>Новых</span></div>
        <div className="admin-stat-card"><b>{data.totalSales}</b><span>Покупок</span></div>
        <div className="admin-stat-card"><b>{(data.totalRevenue || 0).toLocaleString('ru-RU')} ₽</b><span>Оборот</span></div>
        <div className="admin-stat-card"><b>{(data.bonusEarned || 0).toLocaleString('ru-RU')}</b><span>Начислено</span></div>
        <div className="admin-stat-card"><b>{(data.bonusSpent || 0).toLocaleString('ru-RU')}</b><span>Списано</span></div>
      </div>

      <div className="admin-analytics-grid">
      {salesChart.length > 0 && (
        <div className="admin-chart-card">
          <h3>Оборот</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={salesChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={50} />
              <Tooltip />
              <Bar dataKey="revenue" name="₽" fill="#4f6ef7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {bonusChart.length > 0 && (
        <div className="admin-chart-card">
          <h3>Бонусы</h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={bonusChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={50} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="earned" name="Начислено" stroke="#4f6ef7" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="spent" name="Списано" stroke="#e57373" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {regChart.length > 0 && (
        <div className="admin-chart-card">
          <h3>Регистрации</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={regChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Рег." fill="#6b4fd8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.tierDistribution?.length > 0 && (
        <div className="admin-chart-card">
          <h3>По уровням</h3>
          <table className="admin-table-compact">
            <tbody>
              {data.tierDistribution.map((row, i) => (
                <tr key={i}><td>{row.templateName}</td><td>{row.count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.topCustomers?.length > 0 && (
        <div className="admin-chart-card admin-chart-wide">
          <h3>Топ клиентов</h3>
          <table className="admin-table-compact">
            <thead><tr><th>Клиент</th><th>Оборот</th><th>Баланс</th></tr></thead>
            <tbody>
              {data.topCustomers.map((c, i) => (
                <tr key={i}>
                  <td>{c.name} {c.surname}</td>
                  <td>{(c.saleAmount || 0).toLocaleString('ru-RU')} ₽</td>
                  <td>{(c.balance || 0).toLocaleString('ru-RU')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default AnalyticsTab;
